import { dimensionLabel, pickGallery, pickHero } from "../../lib/catalog";
import { pick } from "../../lib/i18n";
import type { CatalogBrandOption, CatalogItem } from "../../types";

const copy = {
  pl: { productWord: (n: number): string => n === 1 ? "produkt" : "produktów", noVisualsProduct: "Brak grafik dla tego produktu — wygeneruj je w karcie produktu", features: "Cechy", dimensions: "Wymiary", variants: "Warianty", noVisualsShort: "Brak grafik — wygeneruj je w karcie produktu" },
  en: { productWord: (n: number): string => n === 1 ? "product" : "products", noVisualsProduct: "No visuals for this product — generate them on the product card", features: "Features", dimensions: "Dimensions", variants: "Variants", noVisualsShort: "No visuals — generate them on the product card" },
};

type Props = {
  name: string;
  brand: CatalogBrandOption | null;
  layout: string;
  items: CatalogItem[];
};

export default function CatalogDocument({ name, brand, layout, items }: Props) {
  const t = pick(copy);
  if (items.length === 0) return null;

  return (
    <div className="catalog-doc">
      <div className="catalog-page catalog-cover">
        {brand?.logo_url && (
          <img
            src={brand.logo_url}
            alt={brand.name}
            onError={(event) => {
              (event.target as HTMLImageElement).style.display = "none";
            }}
          />
        )}
        <h1>{name}</h1>
        <p>
          {items.length} {t.productWord(items.length)}
          {brand ? ` · ${brand.name}` : ""}
        </p>
      </div>

      {layout === "grid" ? (
        <GridPages items={items} />
      ) : (
        items.map((item) => <ProductPage key={item.product_id} item={item} />)
      )}
    </div>
  );
}

function ProductPage({ item }: { item: CatalogItem }) {
  const t = pick(copy);
  const hero = pickHero(item.outputs, item.hero_output_id);
  const gallery = pickGallery(item.outputs, hero?.id);
  const dimensions = Object.entries(item.dimensions).filter(([, value]) => value !== null && value !== "");

  return (
    <div className="catalog-page">
      {hero ? (
        <img className="catalog-hero" src={hero.image_url} alt={item.name} />
      ) : (
        <div
          className="catalog-hero"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 18mm", color: "var(--muted)", fontSize: 13, lineHeight: 1.5 }}
        >
          {t.noVisualsProduct}
        </div>
      )}
      <div className="catalog-product-name">{item.name}</div>
      <div className="catalog-meta">
        <div>
          <div className="catalog-section-title">{t.features}</div>
          <ul className="catalog-features">
            {item.features.slice(0, 6).map((feature, index) => (
              <li key={index}>
                <strong>{feature.name}</strong>
                {feature.description ? ` — ${feature.description}` : ""}
              </li>
            ))}
          </ul>
        </div>
        <div>
          {dimensions.length > 0 && (
            <>
              <div className="catalog-section-title">{t.dimensions}</div>
              {dimensions.map(([key, value]) => (
                <div className="catalog-dim-row" key={key}>
                  <span>{dimensionLabel(key)}</span>
                  <span>{value}{key.endsWith("_cm") ? " cm" : ""}</span>
                </div>
              ))}
            </>
          )}
          {item.variants.length > 0 && (
            <div style={{ marginTop: dimensions.length > 0 ? 14 : 0 }}>
              <div className="catalog-section-title">{t.variants}</div>
              <div className="catalog-variants">
                {item.variants.map((variant, index) => (
                  <span className="catalog-variant-chip" key={index}>{variant.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {gallery.length > 0 && (
        <div className="catalog-gallery">
          {gallery.map((output) => (
            <img key={output.id} src={output.image_url} alt="" />
          ))}
        </div>
      )}
    </div>
  );
}

function GridPages({ items }: { items: CatalogItem[] }) {
  const t = pick(copy);
  const pages: CatalogItem[][] = [];
  for (let index = 0; index < items.length; index += 2) {
    pages.push(items.slice(index, index + 2));
  }

  return (
    <>
      {pages.map((pair, pageIndex) => (
        <div className="catalog-page catalog-grid-page" key={pageIndex}>
          {pair.map((item) => {
            const hero = pickHero(item.outputs, item.hero_output_id);
            return (
              <div className="catalog-grid-card" key={item.product_id}>
                {hero ? (
                  <img className="catalog-hero" src={hero.image_url} alt={item.name} />
                ) : (
                  <div
                    className="catalog-hero"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 10mm", color: "var(--muted)", fontSize: 12, lineHeight: 1.4 }}
                  >
                    {t.noVisualsShort}
                  </div>
                )}
                <div className="catalog-product-name">{item.name}</div>
                <ul className="catalog-features">
                  {item.features.slice(0, 3).map((feature, index) => (
                    <li key={index}><strong>{feature.name}</strong></li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}
